// Site Manager guided induction builder — self-service flow that clones a Priority
// Risk master (or an existing client template), lets the manager toggle sections on/off,
// add Assembly Points, decide whether to collect freeform training certificates during
// the induction, write Contractor Instructions, then generates the site's QR poster.
// Deliberately separate from the generic admin Templates tab (InductionsPage.jsx).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ShieldCheck, ArrowLeft, ArrowRight, Loader2, MapPin,
    ClipboardCheck, FileSignature, CheckCircle2, Building, ChevronDown, ChevronUp,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SiteQRPoster from "@/components/SiteQRPoster";
import AssemblyPointsEditor from "@/components/onsite/AssemblyPointsEditor";

const BRAND = "var(--brand-accent, #C75A1B)";
const STEPS = [
    { id: "start", label: "Start" },
    { id: "sections", label: "Sections" },
    { id: "assembly", label: "Assembly points" },
    { id: "certificates", label: "Certificates" },
    { id: "contractor", label: "Contractor instructions" },
    { id: "review", label: "Review" },
];

export default function BuildInductionWizard() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const clientId = user?.client_id || (user?.client_ids || [])[0] || "";
    const [sites, setSites] = useState([]);
    const [siteId, setSiteId] = useState("");
    const [masters, setMasters] = useState([]);
    const [clientTemplates, setClientTemplates] = useState([]);
    const [loading, setLoading] = useState(true);

    const [stepIdx, setStepIdx] = useState(0);
    const [name, setName] = useState("");
    const [cloneFrom, setCloneFrom] = useState("");
    const [busy, setBusy] = useState(false);
    const [template, setTemplate] = useState(null);
    const [excluded, setExcluded] = useState(new Set());
    const [excludedFields, setExcludedFields] = useState(new Set());
    const [expandedSections, setExpandedSections] = useState(new Set());
    const [assemblyPoints, setAssemblyPoints] = useState([]);
    const [wantCerts, setWantCerts] = useState(true);
    const [contractorText, setContractorText] = useState("");
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!clientId) return;
        Promise.all([
            api.get(`/inductions/sites?client_id=${clientId}`),
            api.get("/inductions/masters"),
            api.get(`/inductions/templates?client_id=${clientId}`),
        ]).then(([s, m, t]) => {
            setSites(s.data || []);
            if ((s.data || [])[0]) setSiteId(s.data[0].id);
            setMasters(m.data || []);
            setClientTemplates(t.data || []);
        }).catch(() => toast.error("Could not load your sites/templates")).finally(() => setLoading(false));
    }, [clientId]);

    const site = sites.find((s) => s.id === siteId);
    const step = STEPS[stepIdx].id;
    const goNext = () => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
    const goBack = () => setStepIdx((i) => Math.max(i - 1, 0));

    async function createTemplate() {
        if (!siteId) { toast.error("Select a site first"); return; }
        if (!name.trim()) { toast.error("Give your induction a name"); return; }
        setBusy(true);
        try {
            const r = await api.post("/inductions/templates", { client_id: clientId, name: name.trim(), clone_from: cloneFrom || null });
            setTemplate(r.data);
            setAssemblyPoints((site?.assembly_points || []).map((a) => ({ ...a })));
            goNext();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not create your induction"); }
        setBusy(false);
    }

    async function finish() {
        setBusy(true);
        try {
            const kept = (template.sections || []).map((s) => ({
                ...s,
                enabled: !excluded.has(s.id),
                fields: (s.fields || []).map((f) => ({ ...f, enabled: !excludedFields.has(f.id) })),
            }));
            const finalSections = [...kept];
            if (wantCerts) {
                finalSections.push({
                    id: `s${Date.now()}`, title: "Certificates & tickets", order: finalSections.length, key: "certificates",
                    fields: [{ id: `f${Date.now()}`, label: "Upload your training certificates & tickets", field_type: "cert_upload_freeform", required: true, options: [] }],
                });
            }
            if (contractorText.trim()) {
                finalSections.push({
                    id: `s${Date.now() + 1}`, title: "Instructions for Contractors", order: finalSections.length,
                    fields: [{ id: `f${Date.now() + 1}`, label: "Instructions for Contractors", field_type: "instruction", content: contractorText.trim(), required: false, options: [] }],
                });
            }
            await api.patch(`/inductions/templates/${template.id}`, { sections: finalSections });
            await api.put(`/inductions/sites/${siteId}/assembly-points`, {
                blocks: assemblyPoints.map((a) => ({ id: a.id, heading: a.heading, body: a.body, image_path: a.image_path })),
            });
            await api.post("/site-forms", {
                client_id: clientId, site_id: siteId, template_id: template.id, template_code: template.code,
                mode: "individual", label: `${site?.name || "Site"} — ${name.trim()}`,
            });
            setTemplate((t) => ({ ...t, sections: finalSections }));
            setDone(true);
            toast.success("Your site induction is ready");
        } catch (e) { toast.error(e.response?.data?.detail || "Could not finish your induction"); }
        setBusy(false);
    }

    if (loading) return <div className="text-center py-16"><Loader2 className="animate-spin inline" size={22} /></div>;
    if (!clientId) return <Empty msg="No client scope found for your account." />;

    return (
        <div className="space-y-5 pb-10 max-w-2xl mx-auto" data-testid="build-induction-wizard">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl grid place-items-center text-white" style={{ background: BRAND }}><ShieldCheck size={20} /></div>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-[#1F1F1F]">Build my site induction</h1>
                    <p className="text-[12.5px] text-[#6F6A64]">Clone a master, make it yours, and get your site's QR poster.</p>
                </div>
            </div>

            {!done && <ProgressBar stepIdx={stepIdx} />}

            {done ? (
                <StepDone clientId={clientId} site={site} template={template} name={name.trim()} navigate={navigate} />
            ) : step === "start" ? (
                <StepStart sites={sites} siteId={siteId} setSiteId={setSiteId} name={name} setName={setName}
                    cloneFrom={cloneFrom} setCloneFrom={setCloneFrom} masters={masters} clientTemplates={clientTemplates}
                    busy={busy} onNext={createTemplate} />
            ) : step === "sections" ? (
                <StepSections template={template} excluded={excluded} setExcluded={setExcluded}
                    excludedFields={excludedFields} setExcludedFields={setExcludedFields}
                    expandedSections={expandedSections} setExpandedSections={setExpandedSections}
                    onBack={goBack} onNext={goNext} />
            ) : step === "assembly" ? (
                <StepAssembly siteId={siteId} points={assemblyPoints} setPoints={setAssemblyPoints} onBack={goBack} onNext={goNext} />
            ) : step === "certificates" ? (
                <StepCertificates wantCerts={wantCerts} setWantCerts={setWantCerts} onBack={goBack} onNext={goNext} />
            ) : step === "contractor" ? (
                <StepContractor text={contractorText} setText={setContractorText} onBack={goBack} onNext={goNext} />
            ) : (
                <StepReview template={template} excluded={excluded} excludedFields={excludedFields} assemblyPoints={assemblyPoints}
                    wantCerts={wantCerts} contractorText={contractorText} busy={busy} onBack={goBack} onFinish={finish} />
            )}
        </div>
    );
}

function ProgressBar({ stepIdx }) {
    return (
        <div className="flex items-center gap-1" data-testid="wizard-progress">
            {STEPS.map((s, i) => (
                <div key={s.id} className="flex-1">
                    <div className="h-1.5 rounded-full" style={{ background: i <= stepIdx ? BRAND : "#E5E0DA" }} />
                    <div className="text-[10px] font-semibold mt-1 truncate" style={{ color: i === stepIdx ? BRAND : "#9A968F" }}>{s.label}</div>
                </div>
            ))}
        </div>
    );
}

function Card({ children }) {
    return <div className="bg-white border rounded-[13px] p-5 shadow-brand-sm space-y-4" style={{ borderColor: "#E5E0DA" }}>{children}</div>;
}
function NavRow({ onBack, onNext, nextLabel = "Continue", busy, disabled, testidNext = "wizard-next" }) {
    return (
        <div className="flex justify-between pt-2">
            {onBack ? (
                <button data-testid="wizard-back" onClick={onBack} className="text-[13px] font-semibold px-4 py-2.5 rounded-lg border inline-flex items-center gap-1.5" style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}>
                    <ArrowLeft size={14} /> Back
                </button>
            ) : <span />}
            <button data-testid={testidNext} onClick={onNext} disabled={busy || disabled}
                className="text-[13px] font-semibold px-5 py-2.5 rounded-lg text-white inline-flex items-center gap-1.5 disabled:opacity-60" style={{ background: BRAND }}>
                {busy ? <Loader2 className="animate-spin" size={14} /> : null} {nextLabel} <ArrowRight size={14} />
            </button>
        </div>
    );
}

function StepStart({ sites, siteId, setSiteId, name, setName, cloneFrom, setCloneFrom, masters, clientTemplates, busy, onNext }) {
    return (
        <Card>
            <label className="block">
                <span className="text-[12.5px] font-semibold text-[#1F1F1F]">Which site is this for?</span>
                <select data-testid="wizard-site-select" value={siteId} onChange={(e) => setSiteId(e.target.value)}
                    className="w-full h-11 px-3 rounded-lg border text-[14px] mt-1.5 bg-white" style={{ borderColor: "#E5E0DA" }}>
                    <option value="">— Select a site —</option>
                    {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
            </label>
            <label className="block">
                <span className="text-[12.5px] font-semibold text-[#1F1F1F]">Name this induction</span>
                <input data-testid="wizard-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sun Gate House — Site Induction"
                    className="w-full h-11 px-3 rounded-lg border text-[14px] mt-1.5" style={{ borderColor: "#E5E0DA" }} />
            </label>
            <label className="block">
                <span className="text-[12.5px] font-semibold text-[#1F1F1F]">Start from</span>
                <select data-testid="wizard-clone-from" value={cloneFrom} onChange={(e) => setCloneFrom(e.target.value)}
                    className="w-full h-11 px-3 rounded-lg border text-[14px] mt-1.5 bg-white" style={{ borderColor: "#E5E0DA" }}>
                    <option value="">Blank induction</option>
                    {masters.length > 0 && <optgroup label="Priority Risk masters">
                        {masters.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </optgroup>}
                    {clientTemplates.length > 0 && <optgroup label="Your existing inductions">
                        {clientTemplates.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </optgroup>}
                </select>
                <p className="text-[11px] text-[#9A968F] mt-1.5">You'll pick which sections to keep on the next step.</p>
            </label>
            <NavRow onNext={onNext} nextLabel="Clone & continue" busy={busy} testidNext="wizard-start-next" />
        </Card>
    );
}

function StepSections({ template, excluded, setExcluded, excludedFields, setExcludedFields, expandedSections, setExpandedSections, onBack, onNext }) {
    const sections = template?.sections || [];

    const toggleSection = (s) => {
        const wasKept = !excluded.has(s.id);
        const fieldIds = (s.fields || []).map((f) => f.id);
        setExcluded((prev) => {
            const next = new Set(prev);
            if (wasKept) next.add(s.id); else next.delete(s.id);
            return next;
        });
        setExcludedFields((prev) => {
            const next = new Set(prev);
            fieldIds.forEach((id) => { if (wasKept) next.add(id); else next.delete(id); });
            return next;
        });
    };
    const toggleField = (fieldId) => setExcludedFields((prev) => {
        const next = new Set(prev);
        if (next.has(fieldId)) next.delete(fieldId); else next.add(fieldId);
        return next;
    });
    const toggleExpand = (sectionId) => setExpandedSections((prev) => {
        const next = new Set(prev);
        if (next.has(sectionId)) next.delete(sectionId); else next.add(sectionId);
        return next;
    });

    return (
        <Card>
            <p className="text-[12.5px] text-[#6F6A64]">Everything's ticked by default. Untick a section to leave it out entirely, or drop it down to fine-tune which questions inside it are included.</p>
            <div className="space-y-2" data-testid="wizard-sections-list">
                {sections.map((s) => {
                    const kept = !excluded.has(s.id);
                    const isOpen = expandedSections.has(s.id);
                    const fields = s.fields || [];
                    const keptCount = fields.filter((f) => !excludedFields.has(f.id)).length;
                    return (
                        <div key={s.id} data-testid={`wizard-section-${s.id}`} className="border rounded-lg overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                            <div className="flex items-center gap-3 px-3 py-2.5" style={{ background: kept ? "#fff" : "#FAF8F5" }}>
                                <input data-testid={`wizard-section-check-${s.id}`} type="checkbox" checked={kept} onChange={() => toggleSection(s)} className="w-4 h-4 flex-shrink-0" />
                                <button type="button" data-testid={`wizard-section-expand-${s.id}`} onClick={() => toggleExpand(s.id)}
                                    className="flex-1 flex items-center gap-2 text-left min-w-0">
                                    <span className="text-[13.5px] font-medium truncate" style={{ color: kept ? "#1F1F1F" : "#9A968F" }}>{s.title}</span>
                                    <span className="text-[11px] flex-shrink-0" style={{ color: "#9A968F" }}>{kept ? `${keptCount}/${fields.length}` : fields.length} item(s)</span>
                                    <span className="ml-auto flex-shrink-0 text-[#9A968F]">{isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
                                </button>
                            </div>
                            {isOpen && (
                                <div data-testid={`wizard-section-fields-${s.id}`} className="border-t px-3 py-2 space-y-1" style={{ borderColor: "#F0EDE8", background: "#FCFBF9" }}>
                                    {fields.length === 0 ? (
                                        <p className="text-[11.5px] text-[#9A968F] py-1">No questions in this section.</p>
                                    ) : fields.map((f) => (
                                        <label key={f.id} data-testid={`wizard-field-${f.id}`} className="flex items-center gap-2.5 py-1" style={{ cursor: kept ? "pointer" : "not-allowed" }}>
                                            <input type="checkbox" checked={!excludedFields.has(f.id)} onChange={() => toggleField(f.id)} disabled={!kept} className="w-3.5 h-3.5" />
                                            <span className="text-[12.5px] truncate" style={{ color: kept && !excludedFields.has(f.id) ? "#1F1F1F" : "#9A968F" }}>{f.label || f.content || "Untitled question"}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
                {sections.length === 0 && <Empty msg="This template has no sections yet — you can add them via Inductions → Templates later." />}
            </div>
            <NavRow onBack={onBack} onNext={onNext} testidNext="wizard-sections-next" />
        </Card>
    );
}

function StepAssembly({ siteId, points, setPoints, onBack, onNext }) {
    return (
        <Card>
            <p className="text-[12.5px] text-[#6F6A64] flex items-center gap-1.5"><MapPin size={13} style={{ color: BRAND }} /> Where should everyone gather in an emergency? Add each assembly point with directions and, ideally, a photo.</p>
            <AssemblyPointsEditor siteId={siteId} points={points} setPoints={setPoints} brand={BRAND} testidPrefix="wizard-assembly" />
            <NavRow onBack={onBack} onNext={onNext} testidNext="wizard-assembly-next" />
        </Card>
    );
}

function StepCertificates({ wantCerts, setWantCerts, onBack, onNext }) {
    return (
        <Card>
            <div className="flex items-start gap-3">
                <ClipboardCheck size={22} style={{ color: BRAND }} className="mt-0.5" />
                <div>
                    <h3 className="text-[14px] font-semibold text-[#1F1F1F]">Inductee training certificates</h3>
                    <p className="text-[12.5px] text-[#6F6A64] mt-1">Ask everyone completing this induction to upload their own tickets/certificates (CSCS, First Aid, plant licences…) on their phone, right there in the induction. They name what it is — there's no fixed list.</p>
                </div>
            </div>
            <div className="flex gap-2">
                {[[true, "Yes — collect certificates"], [false, "No — skip this"]].map(([v, l]) => (
                    <button key={String(v)} data-testid={`wizard-certs-${v}`} onClick={() => setWantCerts(v)}
                        className="flex-1 h-11 rounded-lg border text-[13px] font-semibold"
                        style={wantCerts === v ? { background: BRAND, color: "#fff", borderColor: BRAND } : { borderColor: "#E5E0DA", color: "#1F1F1F" }}>
                        {l}
                    </button>
                ))}
            </div>
            <NavRow onBack={onBack} onNext={onNext} testidNext="wizard-certs-next" />
        </Card>
    );
}

function StepContractor({ text, setText, onBack, onNext }) {
    return (
        <Card>
            <div className="flex items-start gap-3">
                <FileSignature size={22} style={{ color: BRAND }} className="mt-0.5" />
                <div>
                    <h3 className="text-[14px] font-semibold text-[#1F1F1F]">Instructions for Contractors</h3>
                    <p className="text-[12.5px] text-[#6F6A64] mt-1">Anything contractors specifically need to know — permits to work, welfare facilities, parking, out-of-bounds areas. Shown to everyone completing this induction. Leave blank to skip.</p>
                </div>
            </div>
            <textarea data-testid="wizard-contractor-text" value={text} onChange={(e) => setText(e.target.value)} rows={8}
                placeholder="e.g. **Permits to work** are required for all hot works...\n- Welfare cabin is behind Block A\n- No parking on site — use the visitor car park"
                className="w-full px-3 py-2.5 rounded-lg border text-[13.5px] leading-relaxed" style={{ borderColor: "#E5E0DA" }} />
            <p className="text-[11px] text-[#9A968F]">Tip: wrap text in **double asterisks** for bold, and start a line with "- " for a bullet point.</p>
            <NavRow onBack={onBack} onNext={onNext} testidNext="wizard-contractor-next" />
        </Card>
    );
}

function StepReview({ template, excluded, excludedFields, assemblyPoints, wantCerts, contractorText, busy, onBack, onFinish }) {
    const kept = (template?.sections || []).filter((s) => !excluded.has(s.id));
    const totalFields = kept.reduce((acc, s) => acc + (s.fields || []).length, 0);
    const keptFields = kept.reduce((acc, s) => acc + (s.fields || []).filter((f) => !excludedFields.has(f.id)).length, 0);
    return (
        <Card>
            <h3 className="text-[14px] font-semibold text-[#1F1F1F]">Review your site induction</h3>
            <ul className="text-[13px] text-[#1F1F1F] space-y-1.5" data-testid="wizard-review-summary">
                <li>✓ {kept.length} section(s) kept, {keptFields}/{totalFields} question(s) included</li>
                <li>✓ {assemblyPoints.length} assembly point(s) added</li>
                <li>{wantCerts ? "✓" : "—"} Freeform training certificate upload {wantCerts ? "enabled" : "skipped"}</li>
                <li>{contractorText.trim() ? "✓" : "—"} Instructions for Contractors {contractorText.trim() ? "added" : "skipped"}</li>
            </ul>
            <p className="text-[11.5px] text-[#9A968F]">Finishing will save your induction and generate a printable QR poster for this site straight away.</p>
            <NavRow onBack={onBack} onNext={onFinish} nextLabel="Finish & get my QR" busy={busy} testidNext="wizard-finish" />
        </Card>
    );
}

function StepDone({ clientId, site, template, name, navigate }) {
    return (
        <div className="space-y-4" data-testid="wizard-done">
            <div className="rounded-xl border p-4 flex items-center gap-3" style={{ borderColor: "#B7E0C4", background: "#EAF6EE" }}>
                <CheckCircle2 size={22} style={{ color: "#2E7D4F" }} />
                <div>
                    <div className="text-[13.5px] font-semibold text-[#1F1F1F]">"{name}" is ready</div>
                    <div className="text-[12px] text-[#6F6A64]">Print the poster below and put it up at the site entrance for {site?.name}.</div>
                </div>
            </div>
            <SiteQRPoster clientId={clientId} siteId={site?.id} siteName={site?.name} templateId={template?.id} templateCode={template?.code} templateLabel={name} />
            <div className="flex items-center gap-2">
                <button data-testid="wizard-done-dashboard" onClick={() => navigate("/app/dashboard")}
                    className="text-[13px] font-semibold px-4 py-2.5 rounded-lg border" style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}>
                    Back to my dashboard
                </button>
                <button data-testid="wizard-done-inductions" onClick={() => navigate("/app/inductions")}
                    className="text-[13px] font-semibold px-4 py-2.5 rounded-lg text-white inline-flex items-center gap-1.5" style={{ background: BRAND }}>
                    <Building size={14} /> Fine-tune further
                </button>
            </div>
        </div>
    );
}

function Empty({ msg }) { return <div className="text-center text-[13px] text-[#9A968F] py-10 border border-dashed rounded-xl" style={{ borderColor: "#E5E0DA" }}>{msg}</div>; }
